/**
 * PipelineView.js
 * Detail view for a multi-route integration pipeline.
 *
 * Layout:
 *   â”Œâ”€ Header bar â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”
 *   â”‚  Back  |  Pipeline name  |  badge  |  status  |  created              â”‚
 *   â””â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”˜
 *   â”Œâ”€ Canvas (left 55%) â”€â”€â”€â”€â”  â”Œâ”€ Connections panel (right 45%) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”
 *   â”‚  ReactFlow workflow    â”‚  â”‚  Tabs (Connection 1, 2, …)               â”‚
 *   â”‚  graph (read‘only)     â”‚  â”‚  â”Œ Source → Destination meta â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â” â”‚
 *   â”‚                        â”‚  â”‚  â”‚  Source / Destination / Status / Runâ”‚ â”‚
 *   â”‚                        â”‚  â”‚  â””â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ â”˜ â”‚
 *   â”‚                        â”‚  â”‚  Field Mapping                           â”‚
 *   â”‚                        â”‚  â”‚  â”Œ source_label → destination_label â”€â”€â”€â” â”‚
 *   â”‚                        â”‚  â”‚  â”‚  ...rows...                         â”‚ â”‚
 *   â”‚                        â”‚  â”‚  â””â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”˜ â”‚
 *   â””â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”˜  â””â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”˜
 */
import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUserInfo } from "@redux/slices/authSlice";
import { Bot } from "lucide-react";
import { toast } from "react-toastify";
import ReactFlow, {
  Background,
  Controls,
  MarkerType,
  Handle,
  Position,
  ReactFlowProvider,
} from "reactflow";
import "reactflow/dist/style.css";
import { getPipelineDetail } from "@services/httpservice";
import { PIPELINE_NODE_TYPES } from "../../components/misc/Workflow/PipelineNodes";
import ScrollContainer from "@components/misc/ScrollContainer";
import D2IntegrationDiagram from "@components/agent/D2IntegrationDiagram";

// â”€â”€â”€ Build ReactFlow graph from backend data â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// ─── Clean box nodes removed: now using PIPELINE_NODE_TYPES from PipelineNodes.js

// ─── Mapping canvas node types ────────────────────────────────────────────────
const SrcFieldNode = ({ data }) => (
  <div style={{
    border: '1.5px solid #93c5fd', borderRadius: '5px', background: 'white',
    padding: '5px 12px', fontSize: '12px', fontWeight: 600, color: '#1e40af',
    whiteSpace: 'nowrap', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', minWidth: '110px',
  }}>
    {data.label}
    <Handle type="source" position={Position.Right}
      style={{ background: '#3b82f6', border: '2px solid white', width: 9, height: 9 }} />
  </div>
);

const DstFieldNode = ({ data }) => (
  <div style={{
    border: '1.5px solid #93c5fd', borderRadius: '5px', background: 'white',
    padding: '5px 12px', fontSize: '12px', fontWeight: 600, color: '#1e40af',
    whiteSpace: 'nowrap', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', minWidth: '110px',
  }}>
    <Handle type="target" position={Position.Left}
      style={{ background: '#3b82f6', border: '2px solid white', width: 9, height: 9 }} />
    {data.label}
  </div>
);

const PlatformLabelNode = ({ data }) => (
  <div style={{
    border: `2px solid ${data.color || '#1d4ed8'}`,
    borderRadius: '6px',
    background: data.color || '#1d4ed8',
    padding: '7px 18px',
    fontSize: '13px',
    fontWeight: 700,
    color: 'white',
    textAlign: 'center',
    minWidth: '110px',
    boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
  }}>
    {data.label}
  </div>
);

const MAPPING_NODE_TYPES = {
  srcField:      SrcFieldNode,
  dstField:      DstFieldNode,
  platformLabel: PlatformLabelNode,
};

// ─── Build mapping ReactFlow graph ────────────────────────────────────────────
function buildMappingGraph(mappingRows, srcPlatform, dstPlatform) {
  const nodes = [];
  const edges = [];
  const xSrc  = 20;
  const xDst  = 310;
  const yPlat = 10;
  const yStart = 80;
  const yStep  = 52;

  nodes.push({
    id: 'mp-src-plat', type: 'platformLabel',
    position: { x: xSrc, y: yPlat },
    data: { label: srcPlatform || 'Source', color: '#1d4ed8' },
    draggable: false, selectable: false,
  });
  nodes.push({
    id: 'mp-dst-plat', type: 'platformLabel',
    position: { x: xDst, y: yPlat },
    data: { label: dstPlatform || 'Destination', color: '#1d4ed8' },
    draggable: false, selectable: false,
  });

  mappingRows.forEach((row, i) => {
    const y = yStart + i * yStep;
    nodes.push({
      id: `mp-src-${i}`, type: 'srcField',
      position: { x: xSrc, y },
      data: { label: row.source },
      draggable: false, selectable: false,
    });
    nodes.push({
      id: `mp-dst-${i}`, type: 'dstField',
      position: { x: xDst, y },
      data: { label: row.destination },
      draggable: false, selectable: false,
    });
    edges.push({
      id: `mp-e-${i}`,
      source: `mp-src-${i}`,
      target: `mp-dst-${i}`,
      type: 'default',
      style: { stroke: '#60a5fa', strokeWidth: 1.5, strokeDasharray: '6,3' },
      markerEnd: { type: MarkerType.ArrowClosed, color: '#3b82f6', width: 14, height: 14 },
    });
  });

  return { nodes, edges };
}

// ─── Mapping canvas component ─────────────────────────────────────────────────
const MappingCanvas = ({ mappingRows, srcPlatform, dstPlatform }) => {
  const { nodes: mapNodes, edges: mapEdges } = useMemo(
    () => buildMappingGraph(mappingRows, srcPlatform, dstPlatform),
    [mappingRows, srcPlatform, dstPlatform]
  );
  const canvasH = Math.max(280, mappingRows.length * 52 + 130);

  return (
    <ReactFlowProvider>
      <div style={{ height: canvasH, width: '100%' }}>
        <ReactFlow
          nodes={mapNodes}
          edges={mapEdges}
          nodeTypes={MAPPING_NODE_TYPES}
          fitView
          fitViewOptions={{ padding: 0.18 }}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          zoomOnScroll={false}
          panOnScroll={false}
          panOnDrag={false}
          preventScrolling={false}
          proOptions={{ hideAttribution: true }}
        >
          <Background variant="dots" gap={18} size={1} color="#f0f0f0" />
        </ReactFlow>
      </div>
    </ReactFlowProvider>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
function buildReactFlowGraph(graphNodes, graphEdges, legs, pipelineType) {
  // Always generate a fresh vertical layout from legs so positions are consistent.
  if (!legs || legs.length === 0) return { nodes: [], edges: [] };

  const rfNodes = [];
  const rfEdges = [];

  // ── Sequential (A → B → C → N) — NEW, does not affect fan_out / fan_in below ──
  if (pipelineType === "sequential") {
    // Sort legs by leg_order (step_order) ascending
    const sortedLegs = [...legs].sort(
      (a, b) => (a.leg_order ?? 0) - (b.leg_order ?? 0)
    );
    const stepSpacingX = 280;

    sortedLegs.forEach((leg, idx) => {
      const platform =
        leg.destinationDetails?.destinationType ||
        leg.destinationDetails?.platform ||
        leg.sourceDetails?.platform ||
        `Step ${idx + 1}`;
      const nodeId = `seq-step-${idx}`;
      const prevPlatform = idx > 0
        ? (sortedLegs[idx - 1]?.destinationDetails?.destinationType ||
           sortedLegs[idx - 1]?.destinationDetails?.platform || `Step ${idx}`)
        : null;
      const nextPlatform = idx < sortedLegs.length - 1
        ? (sortedLegs[idx + 1]?.destinationDetails?.destinationType ||
           sortedLegs[idx + 1]?.destinationDetails?.platform || `Step ${idx + 2}`)
        : null;

      rfNodes.push({
        id: nodeId,
        type: "sequential_connector",
        position: { x: idx * stepSpacingX, y: 100 },
        data: {
          label: platform,
          platform,
          stepOrder: idx,
          receivesFrom: prevPlatform,
          passesTo: nextPlatform,
        },
        draggable: false,
        selectable: false,
      });

      if (idx > 0) {
        rfEdges.push({
          id: `seq-e-${idx}`,
          source: `seq-step-${idx - 1}`,
          target: nodeId,
          animated: true,
          label: "enriches →",
          style: { stroke: "#2563eb" },
          markerEnd: { type: MarkerType.ArrowClosed },
        });
      }
    });

    return { nodes: rfNodes, edges: rfEdges };
  }
  // ── End sequential branch ─────────────────────────────────────────────────

  if (pipelineType === "fan_out") {
    const triggerPlatform =
      legs[0]?.triggerDetails?.source_type ||
      legs[0]?.triggerDetails?.triggerType ||
      legs[0]?.triggerDetails?.platform || "Trigger";
    const triggerId = "trigger-0";
    const nodeWidth = 140;
    const spacing = nodeWidth + 40;
    const totalW = legs.length * spacing - 40;    // Center trigger above all connectors
    const triggerX = (legs.length - 1) * spacing / 2;
    rfNodes.push({
      id: triggerId, type: "trigger",
      position: { x: triggerX, y: 30 },
      data: { label: triggerPlatform, platform: triggerPlatform },
      draggable: false, selectable: false,
    });
    legs.forEach((leg, idx) => {
      const dest =
        leg.destinationDetails?.destinationType ||
        leg.destinationDetails?.platform ||
        leg.destinationDetails?.destination_platform ||
        `Destination ${idx + 1}`;
      const nodeId = `connector-${idx}`;
      rfNodes.push({
        id: nodeId, type: "connector",
        position: { x: idx * spacing, y: 220 },
        data: { label: dest, platform: dest, action: leg.destinationDetails?.action || "" },
        draggable: false, selectable: false,
      });
      rfEdges.push({
        id: `e-${idx}`, source: triggerId, target: nodeId,
        animated: true, markerEnd: { type: MarkerType.ArrowClosed },
      });
    });
  } else {
    // fan_in — vertical stack: sources top → aggregator → destination
    const aggId = "aggregator-0";
    legs.forEach((leg, idx) => {
      const src =
        leg.triggerDetails?.source_type ||
        leg.triggerDetails?.triggerType ||
        leg.triggerDetails?.platform ||
        leg.sourceDetails?.platform ||
        `Source ${idx + 1}`;
      const nodeId = `source-${idx}`;
      rfNodes.push({
        id: nodeId, type: "connector",
        position: { x: 150, y: 30 + idx * 150 },
        data: { label: src, platform: src },
        draggable: false, selectable: false,
      });
      rfEdges.push({
        id: `e-src-${idx}`, source: nodeId, target: aggId,
        animated: true, markerEnd: { type: MarkerType.ArrowClosed },
      });
    });
    rfNodes.push({
      id: aggId, type: "aggregator",
      position: { x: 150, y: 30 + legs.length * 150 },
      data: { label: "Aggregator", strategy: "merge" },
      draggable: false, selectable: false,
    });
    const dest =
      legs[0]?.destinationDetails?.destinationType ||
      legs[0]?.destinationDetails?.platform ||
      legs[0]?.destinationDetails?.destination_platform || "Destination";
    rfNodes.push({
      id: "dest-0", type: "connector",
      position: { x: 150, y: 30 + (legs.length + 1) * 150 },
      data: { label: dest, platform: dest },
      draggable: false, selectable: false,
    });
    rfEdges.push({
      id: "e-agg-dest", source: aggId, target: "dest-0",
      animated: true, markerEnd: { type: MarkerType.ArrowClosed },
    });
  }

  return { nodes: rfNodes, edges: rfEdges };
}

// â”€â”€â”€ Parse mappingDetails into clean rows â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// Handles the engine format: [{source: {label}, target: {label}, isDirect, dataTranslation}]
// as well as simple [{source_field, destination_field}] from older formats.
function parseMappingRows(mappingDetails) {
  if (!mappingDetails) return [];
  const raw = Array.isArray(mappingDetails)
    ? mappingDetails
    : Array.isArray(mappingDetails?.mappings)
      ? mappingDetails.mappings
      : Array.isArray(mappingDetails?.field_mappings)
        ? mappingDetails.field_mappings
        : [];

  return raw
    .map((entry, i) => {
      // Engine format from to_engine_dict()
      if (entry && typeof entry === "object" && entry.source && typeof entry.source === "object") {
        const srcLabel = entry.source?.label || entry.source?.data?.label || entry.source?.id || `Source ${i + 1}`;
        const dstLabel = entry.target?.label || entry.target?.data?.label || entry.target?.id || `Destination ${i + 1}`;
        const isDirect = entry.isDirect !== false;
        const transform = !isDirect && entry.dataTranslation?.translationType
          ? entry.dataTranslation.translationType
          : "Direct";
        return { source: srcLabel, destination: dstLabel, transformation: transform };
      }
      // Simple flat format
      if (entry && (entry.source_field || entry.source_label)) {
        return {
          source: entry.source_label || entry.source_field,
          destination: entry.destination_label || entry.destination_field,
          transformation: entry.transformation || "Direct",
        };
      }
      return null;
    })
    .filter(Boolean);
}

// â”€â”€â”€ Connection Details Panel â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const ConnectionPanel = ({ leg, navigate }) => {
  const src =
    leg.triggerDetails?.source_type ||
    leg.triggerDetails?.triggerType ||
    leg.triggerDetails?.platform ||
    leg.sourceDetails?.platform || "\u2014";
  const dst =
    leg.destinationDetails?.destinationType ||
    leg.destinationDetails?.platform ||
    leg.destinationDetails?.destination_platform || "\u2014";

  const mappingRows = parseMappingRows(leg.mappingDetails);
  const srcLabel = src === "\u2014" ? "Source" : src;
  const dstLabel = dst === "\u2014" ? "Destination" : dst;

  return (
    <div className="flex flex-col h-full">
      {/* Field mapping visual canvas */}
      <div className="flex-1 overflow-y-auto">
        <div className="px-4 pt-3 pb-1 flex items-center justify-between">
          <span className="text-xs font-bold text-gray-900 tracking-wide">Field Mapping</span>
          {mappingRows.length > 0 && (
            <span className="text-xs font-semibold text-blue-600">
              {mappingRows.length} field{mappingRows.length !== 1 ? "s" : ""}
            </span>
          )}
        </div>
        {mappingRows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-900">
            <svg className="w-8 h-8 mb-2 opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
            </svg>
            <p className="text-sm">No field mapping configured.</p>
          </div>
        ) : (
          <MappingCanvas
            mappingRows={mappingRows}
            srcPlatform={srcLabel}
            dstPlatform={dstLabel}
          />
        )}
      </div>

      {/* Link to full details */}
      <div className="px-4 py-3 border-t border-gray-100 flex-shrink-0">
        <button
          onClick={() => navigate(`/integrations/${leg.integration_id}/details`)}
          className="text-sm font-semibold text-blue-600 hover:text-blue-800 hover:underline transition-colors"
        >
          View full integration details &rarr;
        </button>
      </div>
    </div>
  );
};

// â”€â”€â”€ Main Page â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const PipelineView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const userInfo = useSelector(selectUserInfo);

  const [pipeline, setPipeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [leftWidth, setLeftWidth] = useState(55); // percent
  const containerRef = useRef(null);
  const dragging = useRef(false);

  const onDragStart = useCallback((e) => {
    e.preventDefault();
    dragging.current = true;
    const rafRef = { current: null };
    const onMove = (ev) => {
      if (!dragging.current || !containerRef.current) return;
      if (rafRef.current) return; // skip if a frame is already pending
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        if (!dragging.current || !containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const pct = ((ev.clientX - rect.left) / rect.width) * 100;
        setLeftWidth(Math.min(80, Math.max(20, pct)));
      });
    };
    const onUp = () => {
      dragging.current = false;
      if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getPipelineDetail(id)
      .then((data) => { if (!cancelled) { setPipeline(data); setLoading(false); } })
      .catch((err) => { if (!cancelled) { setError(err?.message || "Failed to load pipeline."); setLoading(false); } });
    return () => { cancelled = true; };
  }, [id]);

  const { nodes, edges } = useMemo(() => {
    if (!pipeline) return { nodes: [], edges: [] };
    return buildReactFlowGraph(pipeline.graph_nodes, pipeline.graph_edges, pipeline.legs, pipeline.pipeline_type);
  }, [pipeline]);

  const d2Graph = useMemo(() => {
    if (!nodes.length) return null;
    return {
      nodes: nodes.map(n => ({ id: n.id, label: n.data.label, type: n.type })),
      edges: edges.map(e => ({ from: e.source, to: e.target }))
    };
  }, [nodes, edges]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-blue-500 border-t-transparent" />
          <p className="text-sm text-gray-900">Loading workflow…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <p className="text-red-600 font-semibold">{error}</p>
        <button onClick={() => navigate("/integrations/listing")}
          className="rounded-lg bg-blue-600 px-5 py-2 text-sm font-bold text-white hover:bg-blue-700">
          Back to Integrations
        </button>
      </div>
    );
  }
  return (
    <div className="flex flex-col bg-gray-50 full-bleed" style={{ minHeight: "calc(100vh - 64px)" }}>
      {/* â”€â”€ Header â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4 shadow-sm flex-shrink-0">
        <div className="max-w-full flex items-center gap-4 flex-wrap">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 rounded-full bg-blue-600 px-4 py-2 text-sm font-bold text-white hover:bg-blue-700 transition-all font-sans"
          >
            ← Back
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg sm:text-xl font-bold text-gray-900 truncate">{pipeline.name}</h1>
          </div>
          {userInfo?.role?.toLowerCase() !== 'user' && (
            <button
              title={pipeline.agent_session_id ? "Open creation chat" : "Open Agent Chat"}
              onClick={() => navigate(pipeline.agent_session_id ? `/agent?session=${pipeline.agent_session_id}` : '/agent')}
              className="flex items-center justify-center gap-x-2 rounded-full bg-blue-600 border border-blue-700 px-5 py-2.5 text-white text-sm font-bold transition-all duration-200 hover:bg-blue-700 shadow-sm active:scale-95 flex-shrink-0 whitespace-nowrap"
            >
              <Bot className="h-5 w-5" />
              Launch Agent
            </button>
          )}
        </div>

        {/* Pipeline webhook URL banner */}
        {(() => {
          const pipelineUrl = `${(process.env.REACT_APP_API_URL || "http://127.0.0.1:8000").replace(/\/$/, "")}/api/integrations/pipelines/${pipeline.pipeline_id}/execute/`;
          return (
            <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-2.5">
              <div className="flex items-center gap-2 flex-grow overflow-hidden">
                <svg className="w-4 h-4 text-blue-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] sm:text-xs font-bold text-blue-700 uppercase tracking-tight">HubSpot Webhook URL</div>
                  <ScrollContainer className="mt-0.5">
                    <div className="text-xs text-blue-600 font-mono whitespace-nowrap pr-4">{pipelineUrl}</div>
                  </ScrollContainer>
                </div>
              </div>
              <button
                onClick={() => { navigator.clipboard.writeText(pipelineUrl); toast.info("Copied to clipboard!"); }}
                className="w-full sm:w-auto flex-shrink-0 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-blue-700 transition-colors"
                title="Copy URL"
              >
                Copy URL
              </button>
            </div>
          );
        })()}
      </div>

      {/* ── Main Content: split or stack ─────────────────────────────────────────────── */}
      <div 
        ref={containerRef} 
        className="flex flex-col lg:flex-row flex-1 overflow-hidden lg:overflow-visible" 
        style={{ height: "calc(100vh - 170px)" }}
      >
        {/* Left: Canvas */}
        <div
          className="bg-white relative flex flex-col flex-shrink-0 border-b lg:border-b-0 border-gray-200"
          style={{ 
            width: window.innerWidth >= 1024 ? `${leftWidth}%` : '100%', 
            height: window.innerWidth >= 1024 ? '100%' : '350px',
            overflow: 'hidden' 
          }}
        >
          <div className="px-3 py-3 flex items-center gap-2 flex-shrink-0 bg-gray-50 lg:bg-white border-b lg:border-b-0 border-gray-100">
            <span className="text-xs sm:text-sm font-bold text-gray-900 uppercase tracking-tight opacity-70">Workflow Overview</span>
          </div>
          <div className="flex-1 min-h-0 flex flex-col">
            {d2Graph ? (
              <div className="w-full h-full flex flex-col items-center justify-center">
                 <D2IntegrationDiagram flowGraph={d2Graph} fullHeight={true} />
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-900 text-sm">
                No workflow graph available yet.
              </div>
            )}
          </div>
        </div>

        {/* Drag handle - Hidden on mobile */}
        <div
          onMouseDown={onDragStart}
          className="hidden lg:flex flex-shrink-0 items-center justify-center bg-gray-100 hover:bg-blue-100 border-x border-gray-200 cursor-col-resize group"
          style={{ width: '10px' }}
          title="Drag to resize"
        >
          <div className="w-0.5 h-8 rounded-full bg-gray-300 group-hover:bg-blue-400 transition-colors" />
        </div>

        {/* Right: Connections / Mapping */}
        <div className="flex flex-col bg-white overflow-hidden flex-1" style={{ minWidth: 0 }}>
          {/* Active connection detail */}
          <div className="flex-1 overflow-y-auto">
            {pipeline.legs[activeTab] && (
              <ConnectionPanel
                leg={pipeline.legs[activeTab]}
                navigate={navigate}
              />
            )}
          </div>

          {/* Prev / Next navigation */}
          {pipeline.legs.length > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50 flex-shrink-0 sticky bottom-0 z-10">
              <button
                onClick={() => setActiveTab((t) => Math.max(0, t - 1))}
                disabled={activeTab === 0}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold text-blue-600 border border-blue-200 bg-white hover:bg-blue-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors shadow-sm"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
                Prev
              </button>
              <div className="text-xs font-bold text-gray-500 bg-gray-200/50 px-3 py-1 rounded-full">
                <span className="text-blue-600">{activeTab + 1}</span> / {pipeline.legs.length}
              </div>
              <button
                onClick={() => setActiveTab((t) => Math.min(pipeline.legs.length - 1, t + 1))}
                disabled={activeTab === pipeline.legs.length - 1}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold text-blue-600 border border-blue-200 bg-white hover:bg-blue-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors shadow-sm"
              >
                Next
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PipelineView;
